import React, { useEffect, useState } from 'react';
import { Button, Card, ConfirmModal, Typography } from 'src/components/common';
import { venturebanner, ventureideabanner, ventureprototypebanner, venturescalingbanner, venturetractionbanner, winnercupwhite } from 'src/assets/images';
import cx from './venturecard.module.scss';
import getRelativeTime from 'src/components/common/relativeTime/relativeTime';
import { useCopyToClipboard, useGetIdentity } from 'src/hooks';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { setFocusClickPoint, ventureVerseLike, ventureVerseUnLike } from 'src/store/venture/venture.slice';
import { openPopup } from 'src/store/common/common.slice';
import { socketService } from 'src/services/socket.api.service';
import { TVenture } from 'src/services/apiEndpoint.types.ts/ventures/ventures.types';

export default function VentureCard({ venture }: { venture: TVenture }) {
  const { getIdentities } = useGetIdentity();
  const isAuthorized: boolean = useAppSelector((store) => store.auth.isAuthorized);
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { copyToClipboard } = useCopyToClipboard();

  // state 
  const [isLoading, setIsLoading] = useState(true);
  const [like, setLike] = useState<{ count: number, isUserLiked: boolean }>({ count: 0, isUserLiked: false });
  const [show, setShow] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false)

  // constant 
  const { authenticatedId, loggedInUserType } = getIdentities();
  const bannerArr = [{ type: 'Idea', banner: ventureideabanner }, { type: 'Prototype', banner: ventureprototypebanner }, { type: 'Scaling', banner: venturescalingbanner }, { type: 'Early Traction', banner: venturetractionbanner }]
  const banner = venture.banner && isLoading ? venture?.banner : bannerArr.find((item) => item.type === venture.ventureStage)?.banner
  const isTopVentureOfDemoDay = venture?.isSelected
  // handler
  function handleNavigateToLogin() {
    navigate(`/login?prev=${encodeURIComponent('/venture')}`)
  }
  function handleNavigate(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    const targetElement = e.target as HTMLElement;

    // Allow navigation when clicking the comment button
    if (!targetElement.closest('.stop')) {
      if (authenticatedId && (venture.userId === authenticatedId || venture?.companyId === authenticatedId)) {
        navigate(`/my-venture?id=${venture?.id}`);
      } else {
        navigate(`/view-venture?id=${venture?.id}`);
      }
      return;
    }

    // Prevent navigation if another button inside the card is clicked
    if (targetElement.closest(`.${cx.ventureCardButton}`)) {
      e.stopPropagation();
      return;
    }

    // Default navigation for clicking the card
    if (venture.userId === authenticatedId || venture?.companyId === authenticatedId) {
      navigate(`/my-venture?id=${venture?.id}`);
    } else {
      navigate(`/view-venture?id=${venture.id}`);
    }
  }
  function handleShare() {
    // const path = (venture.userId === authenticatedId || venture?.companyId === authenticatedId) ? `${window.location.host}/my-venture?id=${venture?.id}` : `${window.location.host}/view-venture?id=${venture.id}`
    const path = `${window.location.host}/view-venture?id=${venture.id}`;
    copyToClipboard({ text: path });
    dispatch(openPopup({
      popupMessage: 'Link copied successfully!',
      popupType: 'success'
    }))
  }
  function handleLike(e) {
    e?.preventDefault();
    e?.stopPropagation()
    if (!isAuthorized) {
      setShow(true)
      return
    }
    setDisabled(true)
    const payload = {
      ...(!authenticatedId ? {} : loggedInUserType === 'Company' ? { companyId: authenticatedId } : { userId: authenticatedId }),
      entityId: venture?.id,
      entityType: "Venture Verse"
    }
    if (like.isUserLiked) {
      dispatch(ventureVerseUnLike(payload)).then((response) => {
        if (response.status) {
          setLike((prev) => ({ isUserLiked: false, count: prev.count - 1 }))
        }
      }).finally(() => {
        setDisabled(false)
      })
    } else {
      dispatch(ventureVerseLike(payload)).then((response) => {
        if (response.status) {
          setLike((prev) => ({ isUserLiked: true, count: prev.count + 1 }))
        }
        if (venture?.userId !== authenticatedId) {
          const performAction = (userId, userType) => {
            console.log("userId venture", userId);
            const userIds = [userId];
            if (userId !== authenticatedId) {
              socketService.emit("notifications-unread", {
                userIds,
                userType: userType
              });
            }
          };
          performAction(venture?.userId, "User");
        }
      }).finally(() => {
        setDisabled(false)
      })
    }
  }
  function handleComment() {
    dispatch(setFocusClickPoint('comment'))
  }

  // effect 
  useEffect(() => {
    if (venture) {
      setLike({ count: venture?.likesCount, isUserLiked: venture?.likedByUser })
    }
  }, [venture])

  return (
    <>
      <ConfirmModal
        title=""
        description={<p style={{ display: 'flex', alignItems: 'center', flexDirection: 'column', margin: '0' }}>
          <span>To enjoy all the features of <span style={{ color: '#fa4616' }}>htraction</span> </span>
          <span><span style={{ color: '#fa4616' }}>Sign in</span> to Continue</span>
        </p>}
        openConfirmModal={show}
        onClose={() => setShow(false)}
        onSubmit={handleNavigateToLogin}
        submitText="Sign in"
        type="confirm"
      />
      <div className={`${cx.ventureCardOuter}`} onClick={handleNavigate}>
        <img src={banner} alt="banner" onError={() => setIsLoading(false)} />
        {/* Demo Day Sticker */}
        {isTopVentureOfDemoDay && (
          <div className={cx.demoDaySticker}>
            <div className={cx.stickerContent}>
              {/* Replace with your cup icon */}
              <img src={winnercupwhite} alt="winner-cup" />
              <div className={cx.stickerContentBox}>
                <p className={cx.stickerContent1}>TOP VENTURE OF</p>
                <p className={cx.stickerContent2}>DEMO DAY</p>
              </div>
            </div>
          </div>
        )}
        <div className={`${cx.ventureCardContent}`}>
          <h3>{venture.title}</h3>
          <h5>
            {getRelativeTime(new Date(venture?.publishedTimestamp || venture?.createdAt).getTime())} <span>{venture.ventureStage}</span>
          </h5>
          <p>
            {venture.description}
          </p>
          <div className={`${cx.ventureCardButton}`}>
            <Button variant={like.isUserLiked ? 'primary' : 'secondary'} loading={disabled} size="extraSmall" disabled={disabled} className="stop" onClick={handleLike}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7.57248 14.9799C4.95206 14.9799 3.8754 13.1195 5.18956 10.8474L7.31123 7.1899C8.6254 4.91782 10.7708 4.91782 12.085 7.1899L14.2066 10.8474C15.5208 13.1195 14.4441 14.9799 11.8237 14.9799H7.57248Z" stroke={like.isUserLiked ? '#fff' : "#FF6239"} strokeWidth="1.08" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {like.count}
            </Button>
            <Button variant="secondary" size="extraSmall" onClick={handleComment}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4.94028 5.88576H7.09098M4.94028 7.76762H8.704M7.35982 11.5313C9.88389 11.5313 11.9301 9.48518 11.9301 6.96111C11.9301 4.43703 9.88389 2.39087 7.35982 2.39087C4.83575 2.39087 2.78958 4.43703 2.78958 6.96111C2.78958 7.47189 2.87338 7.96311 3.02797 8.42176C3.08615 8.59436 3.11523 8.68066 3.12048 8.74695C3.12566 8.81243 3.12175 8.85833 3.10555 8.92198C3.08915 8.98643 3.05293 9.05346 2.98051 9.18752L2.10105 10.8153C1.97561 11.0475 1.91289 11.1636 1.92692 11.2532C1.93915 11.3313 1.98508 11.4 2.05252 11.4411C2.12995 11.4884 2.26121 11.4748 2.52372 11.4477L5.27717 11.163C5.36055 11.1544 5.40224 11.1501 5.44024 11.1516C5.47762 11.153 5.504 11.1565 5.54045 11.1649C5.57751 11.1734 5.62411 11.1914 5.7173 11.2273C6.22705 11.4237 6.78086 11.5313 7.35982 11.5313Z" stroke="#FF6239" strokeWidth="1.07535" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {venture.commentsCount}
            </Button>
            <Button variant="secondary" style={{ width: '50px' }} className="stop" size="extraSmall" onClick={handleShare}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.7388 14.3598C11.2279 14.3598 10.7937 14.181 10.4362 13.8235C10.0786 13.4659 9.8998 13.0317 9.8998 12.5209C9.8998 12.4493 9.90491 12.3753 9.91512 12.2986C9.92534 12.222 9.94066 12.1531 9.9611 12.0918L5.6395 9.57849C5.46582 9.73174 5.2717 9.85178 5.05715 9.93862C4.84261 10.0255 4.61784 10.0689 4.38286 10.0689C3.87204 10.0689 3.43783 9.89009 3.08025 9.53251C2.72267 9.17493 2.54388 8.74073 2.54388 8.2299C2.54388 7.71908 2.72267 7.28487 3.08025 6.92729C3.43783 6.56972 3.87204 6.39093 4.38286 6.39093C4.61784 6.39093 4.84261 6.43435 5.05715 6.52119C5.2717 6.60803 5.46582 6.72807 5.6395 6.88132L9.9611 4.36805C9.94066 4.30675 9.92534 4.23779 9.91512 4.16116C9.90491 4.08454 9.8998 4.01047 9.8998 3.93895C9.8998 3.42813 10.0786 2.99392 10.4362 2.63634C10.7937 2.27877 11.2279 2.09998 11.7388 2.09998C12.2496 2.09998 12.6838 2.27877 13.0414 2.63634C13.399 2.99392 13.5778 3.42813 13.5778 3.93895C13.5778 4.44978 13.399 4.88398 13.0414 5.24156C12.6838 5.59914 12.2496 5.77793 11.7388 5.77793C11.5038 5.77793 11.279 5.73451 11.0645 5.64767C10.8499 5.56083 10.6558 5.44079 10.4821 5.28754L6.16054 7.80081C6.18098 7.86211 6.1963 7.93107 6.20652 8.00769C6.21673 8.08432 6.22184 8.15839 6.22184 8.2299C6.22184 8.30142 6.21673 8.37549 6.20652 8.45211C6.1963 8.52874 6.18098 8.5977 6.16054 8.659L10.4821 11.1723C10.6558 11.019 10.8499 10.899 11.0645 10.8121C11.279 10.7253 11.5038 10.6819 11.7388 10.6819C12.2496 10.6819 12.6838 10.8607 13.0414 11.2182C13.399 11.5758 13.5778 12.01 13.5778 12.5209C13.5778 13.0317 13.399 13.4659 13.0414 13.8235C12.6838 14.181 12.2496 14.3598 11.7388 14.3598ZM11.7388 4.55195C11.9125 4.55195 12.058 4.4932 12.1755 4.37571C12.293 4.25822 12.3518 4.11264 12.3518 3.93895C12.3518 3.76527 12.293 3.61969 12.1755 3.5022C12.058 3.38471 11.9125 3.32596 11.7388 3.32596C11.5651 3.32596 11.4195 3.38471 11.302 3.5022C11.1845 3.61969 11.1258 3.76527 11.1258 3.93895C11.1258 4.11264 11.1845 4.25822 11.302 4.37571C11.4195 4.4932 11.5651 4.55195 11.7388 4.55195ZM4.38286 8.8429C4.55654 8.8429 4.70213 8.78415 4.81962 8.66666C4.93711 8.54917 4.99586 8.40358 4.99586 8.2299C4.99586 8.05622 4.93711 7.91064 4.81962 7.79315C4.70213 7.67566 4.55654 7.61691 4.38286 7.61691C4.20918 7.61691 4.0636 7.67566 3.94611 7.79315C3.82862 7.91064 3.76987 8.05622 3.76987 8.2299C3.76987 8.40358 3.82862 8.54917 3.94611 8.66666C4.0636 8.78415 4.20918 8.8429 4.38286 8.8429ZM11.7388 13.1338C11.9125 13.1338 12.058 13.0751 12.1755 12.9576C12.293 12.8401 12.3518 12.6945 12.3518 12.5209C12.3518 12.3472 12.293 12.2016 12.1755 12.0841C12.058 11.9666 11.9125 11.9079 11.7388 11.9079C11.5651 11.9079 11.4195 11.9666 11.302 12.0841C11.1845 12.2016 11.1258 12.3472 11.1258 12.5209C11.1258 12.6945 11.1845 12.8401 11.302 12.9576C11.4195 13.0751 11.5651 13.1338 11.7388 13.1338Z" fill="#FF6239" />
              </svg>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
